import { WappMode } from '@weblite-wapps/protocol'
import { setWappOutput } from 'weblite-web-wapprunner'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { getSectionTop, popPanelFromSection } from 'weblite-router'
import {
  WAPP_COMMUNICATE_CORE_CHANGE_MODE,
  WAPP_COMMUNICATE_CORE_CLOSE_X,
  WAPP_COMMUNICATE_CORE_DRAWER_CANCEL,
  WAPP_COMMUNICATE_CORE_DRAWER_DONE,
  WAPP_COMMUNICATE_CORE_INSTANTIATE_WAPP,
  WAPP_COMMUNICATE_CORE_OPEN_IN_DRAWER,
  WAPP_COMMUNICATE_CORE_OPEN_IN_POPUP,
  WAPP_COMMUNICATE_CORE_POPUP_CANCEL,
  WAPP_COMMUNICATE_CORE_POPUP_DONE,
  WAPP_COMMUNICATE_CORE_RUN_WAPP,
  WAPP_COMMUNICATE_CORE_SET_FULLSCREEN,
  WAPP_COMMUNICATE_CORE_SET_WAPP_OUTPUT,
  wappCommunicateCoreInstantiateWapp,
  wappCommunicateCoreOpenInDrawer,
} from '@weblite/wapp-communication-action/core'
import { showWappDrawer } from '$comp/modals/wapp-drawer'
import { WappPanel } from '$logic/wapp-panels'
import { getWapp, instantiateWapp } from '$logic/wapps/service'
import {
  wappInstantiableByIdView,
  wappModesByIdView,
} from '$logic/wapps/wapps.reducer'
import { sendOldActionToWapp } from '$logic/wapp-communication/wapp-communication.publish'
import {
  modeIsFeasibleToChangeTo,
  openWappInDrawer,
  returnOldWappOutputToParent,
} from '$logic/wapp-communication/old-api/wapps.helper'
import { getWis } from '$logic/wises'
import { desktopAppApi } from '$scenes/components/app/app.desktop.api'
import { wappSub } from '$func/subscription'

wappSub.subscribe(
  WAPP_COMMUNICATE_CORE_SET_FULLSCREEN,
  ({ payload: { willBeFullscreen }, meta: { mode } }) => {
    if (mode === WappMode.MAIN)
      desktopAppApi.setWappPanelIsFullscreen(willBeFullscreen)
  },
  true,
)

wappSub.subscribe(
  WAPP_COMMUNICATE_CORE_CHANGE_MODE,
  ({
    payload: { mode: newMode, input },
    meta: { wappId, wisId, mode: oldMode },
  }) => {
    if (!modeIsFeasibleToChangeTo({ oldMode, newMode, wappId })) return

    if (oldMode === WappMode.MAIN && newMode === 'fullscreen') {
      desktopAppApi.setWappPanelIsFullscreen(true)
      return
    }

    WappPanel.openWapp({
      wappId,
      wisId,
      input,
    })
  },
  true,
)

wappSub.subscribe(
  WAPP_COMMUNICATE_CORE_INSTANTIATE_WAPP,
  async ({ payload: { customization, wappId }, meta }) => {
    const wapp = await getWapp(wappId)

    const error = !wapp
      ? 'WappId is not valid'
      : !wappInstantiableByIdView(wappId)
      ? 'Provided wapp is Not instantiable'
      : undefined

    if (error) {
      sendOldActionToWapp(wappCommunicateCoreInstantiateWapp({ error }), meta)
      return
    }

    const wisId = await instantiateWapp(wappId, customization)
    sendOldActionToWapp(
      wappCommunicateCoreInstantiateWapp({ wappId, wisId, customization }),
      meta,
    )
  },
  true,
)

const RUN_WAPP_FEASIBLE_WAPP_MODES = new Set([
  'main',
  'fullscreen',
  'drawer',
  'discover',
])

wappSub.subscribe(
  WAPP_COMMUNICATE_CORE_RUN_WAPP,
  async ({
    payload: { wappId, wisId, mode, input },
    meta: { mode: parentMode },
  }) => {
    if (!RUN_WAPP_FEASIBLE_WAPP_MODES.has(mode)) return

    const wapp = await getWapp(wappId)
    if (!wapp) return

    if (wisId) {
      const wis = await getWis(wisId)
      if (!wis) return
    }

    if (!wappModesByIdView(wappId)?.includes(mode)) return

    if (mode === 'drawer')
      return showWappDrawer({
        title: input && input.headerText,
        wappId,
        wisId,
        input,
      })

    /**
     * mode discover means wapp is going to run in discover section
     */
    // if (mode === 'discover') {
    //   dispatchSetDiscoverWapp({ wappId, input })
    //   await routerSetLeftPanelNavbar('home')
    //   return
    // }

    WappPanel.openWapp({
      wappId,
      wisId,
      input,
    })
  },
  true,
)

wappSub.subscribe(
  WAPP_COMMUNICATE_CORE_CLOSE_X,
  ({ payload: shouldCloseWapp, meta: { mode } }) => {
    // TODO: Consider fullscreen mode too
    if (mode === 'main') {
      if (shouldCloseWapp) WappPanel.closeTopWapp()
      else if (getSectionTop('right') === 'wapp') popPanelFromSection('right')
    }
  },
  true,
)

wappSub.subscribe(
  WAPP_COMMUNICATE_CORE_SET_WAPP_OUTPUT,
  ({ payload: { output }, meta: { wappSessionId } }) => {
    setWappOutput(wappSessionId, output)
  },
  true,
)

wappSub.subscribe(
  WAPP_COMMUNICATE_CORE_OPEN_IN_DRAWER,
  async ({ payload, meta }) => {
    const output = await openWappInDrawer(payload, meta)
    sendOldActionToWapp(wappCommunicateCoreOpenInDrawer(output), meta)
  },
  true,
)

wappSub.subscribe(
  WAPP_COMMUNICATE_CORE_DRAWER_DONE,
  ({ meta, payload }) => {
    if (meta.mode !== 'drawer') return

    const {
      output,
      options: { closeDrawer },
    } = payload

    returnOldWappOutputToParent(meta.wappSessionId, output)

    if (closeDrawer) closeCurrentModal()
  },
  true,
)

wappSub.subscribe(
  WAPP_COMMUNICATE_CORE_DRAWER_CANCEL,
  ({ meta }) => {
    if (meta.mode !== 'drawer') return

    returnOldWappOutputToParent(meta.wappSessionId, 'canceled')
    closeCurrentModal()
  },
  true,
)

wappSub.subscribe(WAPP_COMMUNICATE_CORE_OPEN_IN_POPUP, async () => {}, true)

wappSub.subscribe(
  WAPP_COMMUNICATE_CORE_POPUP_DONE,
  ({ meta }) => {
    // NOTE: Keep until implementing popup mode
    if (meta.mode !== 'drawer') return
    closeCurrentModal()
  },
  true,
)

wappSub.subscribe(
  WAPP_COMMUNICATE_CORE_POPUP_CANCEL,
  ({ meta }) => {
    // NOTE: Keep until implementing popup mode
    if (meta.mode !== 'drawer') return
    closeCurrentModal()
  },
  true,
)
